import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Button,
  Form,
  Modal,
  Table,
  InputGroup,
  FormControl,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { FiEdit } from "react-icons/fi";
import Select from "react-select";
import { Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import Block from "../../components/Block/Block";
import { HiOfficeBuilding } from "react-icons/hi";
import Pagination from "../Constant/Pagination";
import { MultiActionDropdown } from "../../components";
import {
  getAllDepartments,
  createDepartment,
  updateDepartment,
  uploadDepartmentExcel,
  getAllLocations,
} from "../../services/SettingServices";
import { MdAddchart } from "react-icons/md";
import { FiPlus, FiUpload, FiDownload } from "react-icons/fi";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";

const DepartmentSettings = () => {
  const [data, setData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null); // null = all
  const [uploading, setUploading] = useState(false);
  const pageSize = 10;

  const [formData, setFormData] = useState({
    name: "",
    is_active: true,
    location: "",
  });
  const [editData, setEditData] = useState(null);
  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        search,
      };

      if (statusFilter !== null) {
        params.is_active = statusFilter;
      }

      const response = await getAllDepartments(params);
      setData(response.data.results || []);
      setCount(response.data.count || 0);
    } catch (err) {
      toast.error("Failed to fetch departments");
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await getAllLocations();
      setLocations(response.data.results || []);
    } catch (err) {
      toast.error("Failed to fetch locations");
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchLocations();
  }, [page, search, statusFilter]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadDepartmentExcel(file);
      toast.success(response.data.message || "Upload successful");
      await fetchDepartments();
      setShowUploadModal(false);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await getAllDepartments({ export: true });
      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "departments_export.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Export successful!");
    } catch (err) {
      toast.error("Failed to export departments");
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error("Department name is required.");
      return;
    }

    try {
      await createDepartment(formData);
      await fetchDepartments();
      setShowModal(false);
      setFormData({ name: "", is_active: true, location: "" });
      toast.success("Department added successfully!");
    } catch (err) {
      const msg =
        err?.response?.data?.name?.[0] ||
        err?.response?.data?.detail ||
        "Failed to add department";
      toast.error(msg);
    }
  };

  const handleUpdate = async () => {
    if (!editData?.name?.trim()) {
      toast.error("Department name is required.");
      return;
    }

    try {
      await updateDepartment(editData.id, editData);
      await fetchDepartments();
      setShowEditModal(false);
      toast.success("Department updated successfully!");
    } catch (err) {
      const msg =
        err?.response?.data?.name?.[0] ||
        err?.response?.data?.detail ||
        "Failed to update department";
      toast.error(msg);
    }
  };

  const StatusBadge = ({ isActive }) => (
    <span
      className={`badge badge-sm ${
        isActive ? "text-bg-success-soft" : "text-bg-danger-soft"
      }`}
    >
      {isActive ? "Active" : "Inactive"}
    </span>
  );

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Location",
      dataIndex: "location_detail",
      key: "location",
      render: (val) => val?.name || "-",
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      render: (v) => <StatusBadge isActive={v} />,
    },
    {
      key: "actions",
      title: "Action",
      render: (_, record) => (
        <FiEdit
          size={14}
          className="text-primary me-2"
          style={{ cursor: "pointer" }}
          onClick={() => {
            setEditData({
              id: record.id,
              name: record.name,
              is_active: record.is_active,
              location: record.location_detail?.id || "",
            });
            setShowEditModal(true);
          }}
        />
      ),
    },
  ];

  const locationOptions = locations.map((loc) => ({
    value: loc.id,
    label: loc.name,
  }));

  const renderFormFields = () => (
    <>
      <Form.Group>
        <Form.Label>Name</Form.Label>
        <Form.Control
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Enter department name"
        />
      </Form.Group>
      <Form.Group className="mt-3">
        <Form.Label>Location</Form.Label>
        <Select
          options={locationOptions}
          classNamePrefix={"react-select"}
          value={
            locationOptions.find((opt) => opt.value === formData.location) ||
            null
          }
          onChange={(selected) =>
            setFormData({ ...formData, location: selected?.value || "" })
          }
          placeholder="Select Location"
          isClearable
        />
      </Form.Group>
      <Form.Group className="mt-3">
        <Form.Check
          label="Active"
          type="checkbox"
          checked={formData.is_active}
          onChange={(e) =>
            setFormData({ ...formData, is_active: e.target.checked })
          }
        />
      </Form.Group>
    </>
  );

  const renderEditFormFields = () => (
    <>
      <Form.Group>
        <Form.Label>Name</Form.Label>
        <Form.Control
          type="text"
          value={editData?.name || ""}
          onChange={(e) => setEditData({ ...editData, name: e.target.value })}
        />
      </Form.Group>
      <Form.Group className="mt-3">
        <Form.Label>Location</Form.Label>
        <Select
          options={locationOptions}
          value={
            locationOptions.find((opt) => opt.value === editData?.location) ||
            null
          }
          onChange={(selected) =>
            setEditData({ ...editData, location: selected?.value || "" })
          }
          placeholder="Select Location"
          isClearable
          classNamePrefix={"react-select"}
        />
      </Form.Group>
      <Form.Group className="mt-3">
        <Form.Check
          label="Active"
          type="checkbox"
          checked={editData?.is_active || false}
          onChange={(e) =>
            setEditData({ ...editData, is_active: e.target.checked })
          }
        />
      </Form.Group>
    </>
  );

  const renderTable = () => (
    <>
      <div className="table-responsive">
        <table className="table fs-6 mb-0 align-middle">
          <thead className="table-light custom-thead">
            <tr>
              {columns.map((col) => (
                <th key={col.key}>{col.title}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-3">
                  No data found
                </td>
              </tr>
            ) : (
              data.map((row, idx) => (
                <tr key={idx}>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.render
                        ? col.render(row[col.dataIndex], row)
                        : row[col.dataIndex]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-end mt-3">
        <Pagination
          currentPage={page}
          recordsPage={pageSize}
          dataLength={count}
          handlePagination={(selectedObj) => setPage(selectedObj.selected + 1)}
          showPageInfo={false}
        />
      </div>
    </>
  );

  return (
    <>
      <div className="card p-2 mb-2">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-1">
          <div className="flex-grow-1 flex-md-grow-0">
            <h5 className="mb-0 d-flex align-items-center gap-2">
                  <HiOfficeBuilding size={18} className="text-primary" />{" "}
                  Departments
            </h5>
          </div>
          <div className="d-flex gap-2 align-items-center flex-wrap">
              <DebounceSmallSearchInput
                placeholder="Search departments"
                handleSearchChange={(value) => setSearch(value)}
              />
                  {/* Status Filter (Custom Dropdown) */}
                  <GlobalSearchableDropdown
                    options={statusOptions}
                    selected={
                      statusOptions.find(
                        (option) => option.value === statusFilter
                      ) || null
                    }
                    onSelect={(selectedOption) => {
                      setStatusFilter(
                        selectedOption ? selectedOption.value : null
                      );
                    }}
                    placeholder="Status"
                    isClearable={true}
                  />

                <MultiActionDropdown
                  variant="outline-primary"
                  btnText="Add"
                  btnIcon={<MdAddchart />}
                  btnOnClick={() => setShowModal(true)}
                  menuOptions={[
                    {
                      label: "Add",
                      icon: <FiPlus />,
                      onClick: () => setShowModal(true),
                    },
                    {
                      label: "Import",
                      icon: <FiUpload />,
                      onClick: () => setShowUploadModal(true),
                    },
                    ...(typeof handleExport === "function"
                      ? [
                          {
                            label: "Export",
                            icon: <FiDownload />,
                            onClick: handleExport,
                          },
                        ]
                      : []),
                  ]}
                />
              </div>
            </div>
          </div>
      <Container fluid className="p-0">
        <Card>
          <Card.Body className="p-0">
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "110px" }}
              >
                <Spinner animation="border" size={35} color="#255d5c" />
              </div>
            ) : (
              renderTable()
            )}
          </Card.Body>
        </Card>

        {/* Add Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Add Department</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>{renderFormFields()}</Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={handleCreate}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Edit Modal */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Department</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>{renderEditFormFields()}</Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUpdate}>
              Update
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Upload Modal */}
        <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Upload Departments</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Select Excel File</Form.Label>
              <Form.Control
                type="file"
                accept=".xls,.xlsx"
                onChange={handleUpload}
                disabled={uploading}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowUploadModal(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default DepartmentSettings;
